import React, { Fragment, useState, useEffect } from 'react';
import { Field, Formik } from 'formik';
import NumberFormat from 'react-number-format';
import styled from 'styled-components';
import TextField from '@material-ui/core/TextField';
import { Body, Title3 } from '../../../components/Typography';
import { FlatButton } from '../../../components/Buttons';
import appConfig from '../../../../app-config';
import StickyErrorMessageBox from '../../../components/ErrorMessage/StickyErrorMessageBox';
import {
  getPaymentMethodInitialValues,
  shouldValidatePostalCode,
  shouldValidateAddress,
} from '../utils';

const ErrorMesageWrapper = styled.div`
  padding: 0;
  width: 100%;
  margin: 0;
`;
const Wrapper = styled.div`
  display: grid;
  grid-gap: 24px;
  border-radius: 6px;
  padding: 24px 32px 24px 12px;
  margin: auto;
`;

const FieldsWrapper = styled.div`
  display: grid;
  grid-template: repeat(3, 62px) / 2fr 1fr;
  grid-gap: 16px 44px;
`;

const SupportedCardTypeDiv = styled.div`
  display: flex;
  gap: 10px;
`;
const SupportedCardTypeIcon = styled.img`
  width: 40px;
  height: 22px;
  object-fit: ${(props) => (props.cardType == 'amex' ? 'cover' : 'contain')};
`;

const POSTAL_CODE_REGEX = /^([A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)/;

const PaymentMethodForm = ({
  handleCancel,
  handleSubmit,
  isRequesting,
  appSource,
  errorMessage,
  hasError,
  clearErrorMessage,
}) => {
  const supportedCardType = appSource.payment_methods.filter((type) =>
    ['mastercard', 'visa', 'discover', 'amex'].includes(type)
  );
  const renderCardIconClassName = (type) => {
    switch (type) {
      // case 'master':
      case 'mastercard':
        return 'https://s3.amazonaws.com/assets.smoothpay.com/web/general-web-ordering-assets/mc-symbol-opt-45-3-x%403x.png';
      case 'visa':
        return 'https://s3.amazonaws.com/assets.smoothpay.com/web/general-web-ordering-assets/Visa-POS-Graphic_visa_pos_fc.png';
      case 'discover':
        return 'https://s3.amazonaws.com/assets.smoothpay.com/web/general-web-ordering-assets/discover_logo.jpg';
      case 'amex':
        return 'https://s3.amazonaws.com/assets.smoothpay.com/web/general-web-ordering-assets/AXP_BlueBoxLogo_EXTRALARGEscale_RGB_DIGITAL_1600x1600.png';
    }
  };

  const isPostalCodeEnabled = shouldValidatePostalCode();
  const isAddressEnabled = shouldValidateAddress();

  const [cvv, setcvv] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [streetName, setStreetName] = useState('');
  useEffect(() => {
    if (hasError) {
      setcvv('');
      setPostalCode('');
      setStreetNumber('');
      setStreetName('');
    }
  }, [hasError]);
  return (
    <Formik
      initialValues={getPaymentMethodInitialValues()}
      validate={(values) => {
        let errors = {};
        if (!values.card_number) {
          errors.card_number = 'Required';
        } else if (
          values.card_number.toString().length < 15 ||
          values.card_number.toString().length > 16
        ) {
          errors.card_number = 'Invalid card number';
        }
        if (!values.expiry_month) {
          errors.expiry_month = 'Required';
        }
        if (!values.expiry_year) {
          errors.expiry_year = 'Required';
        }
        if (!cvv) {
          errors.cvv = 'Required';
        } else if (cvv.toString().length < 3) {
          errors.cvv = 'Invalid CVV';
        }

        if (values.cardholder_name.length < 4) {
          errors.cardholder_name = 'Minimum of 4 characters';
        }
        if (!values.cardholder_name) {
          errors.cardholder_name = 'Required';
        }
        if (isPostalCodeEnabled) {
          // if (!values.postal_code) {
          if (!postalCode) {
            errors.postal_code = 'Required';
          }
          // else if (!POSTAL_CODE_REGEX.test(values.postal_code)) {
          //   errors.postal_code = 'Invalid Postal Code';
          // }
        }
        if (isAddressEnabled) {
          if (!streetNumber) {
            errors.street_number = 'Required';
          }
          if (!streetName) {
            errors.street_name = 'Required';
          }
        }

        return errors;
      }}
      onSubmit={(values) => {
        const submissionData = {
          ...values,
          cvv,
          ...(isPostalCodeEnabled && { postal_code: postalCode }),
          ...(isAddressEnabled && {
            street_number: streetNumber,
            street_name: streetName,
          }),
        };
        if (hasError) {
          clearErrorMessage();
          handleSubmit(submissionData);
        } else {
          handleSubmit(submissionData);
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          {errorMessage && Object.keys(errorMessage).length > 0 && (
            <ErrorMesageWrapper>
              <StickyErrorMessageBox
                errors={errorMessage}
                marginTop='0'
                messageWidth='100%'
              />
            </ErrorMesageWrapper>
          )}
          <Wrapper>
            <Title3 id='PaymentMethods-FormTitle'>Add Card</Title3>
            <SupportedCardTypeDiv>
              {supportedCardType.map((cardType) => {
                return (
                  <SupportedCardTypeIcon
                    cardType={cardType}
                    src={renderCardIconClassName(cardType)}
                    alt={cardType}
                  />
                );
              })}
            </SupportedCardTypeDiv>
            <FieldsWrapper>
              <NumberFormat
                id='PaymentMethods-CardNumber'
                label='Card Number'
                name='card_number'
                // type="number"
                autoComplete='cc-number'
                value={values.card_number}
                onValueChange={(values) =>
                  setFieldValue('card_number', values.value)
                }
                customInput={TextField}
                required
                autoFocus
                onBlur={handleBlur}
                error={touched.card_number && errors.card_number ? true : false}
                helperText={touched.card_number && errors.card_number}
                format='#### #### #### #### #### ###'
                style={{
                  gridColumn: '1 / -1',
                  fontFamily: appConfig.styleConfig.primaryFontFontFamily,
                }}
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  gridGap: '8px',
                }}
              >
                <NumberFormat
                  id='PaymentMethods-ExpiryMonth'
                  label='MM'
                  name='expiry_month'
                  // type="number"
                  autoComplete='cc-exp-month'
                  value={values.expiry_month}
                  onValueChange={(values) =>
                    setFieldValue('expiry_month', values.value)
                  }
                  customInput={TextField}
                  required
                  onBlur={handleBlur}
                  error={
                    touched.expiry_month && errors.expiry_month ? true : false
                  }
                  helperText={touched.expiry_month && errors.expiry_month}
                  format='##'
                  placeholder='MM'
                  // mask={['M', 'M']}
                  style={{
                    fontFamily: appConfig.styleConfig.primaryFontFontFamily,
                  }}
                />
                <div
                  style={{ paddingTop: '23px', color: 'rgba(0, 0, 0, 0.54)' }}
                >
                  /
                </div>
                <NumberFormat
                  id='PaymentMethods-ExpiryYear'
                  label='YYYY'
                  name='expiry_year'
                  // type="number"
                  autoComplete='cc-exp-year'
                  value={values.expiry_year}
                  onValueChange={(values) =>
                    setFieldValue('expiry_year', values.value)
                  }
                  customInput={TextField}
                  required
                  onBlur={handleBlur}
                  error={
                    touched.expiry_year && errors.expiry_year ? true : false
                  }
                  helperText={touched.expiry_year && errors.expiry_year}
                  format='####'
                  placeholder='YYYY'
                  // mask={['Y', 'Y', 'Y', 'Y']}
                  style={{
                    fontFamily: appConfig.styleConfig.primaryFontFontFamily,
                  }}
                />
              </div>
              <NumberFormat
                id='PaymentMethods-CardCVV'
                label='CVV'
                name='cvv'
                autoComplete='cc-csc'
                value={cvv}
                onValueChange={(values) => {
                  setcvv(values.value);
                }}
                customInput={TextField}
                required
                onBlur={handleBlur}
                error={touched.cvv && errors.cvv ? true : false}
                helperText={touched.cvv && errors.cvv}
                format='####'
                style={{
                  fontFamily: appConfig.styleConfig.primaryFontFontFamily,
                }}
              />
              <TextField
                id='PaymentMethods-CardHolderName'
                label='Cardholder Name'
                name='cardholder_name'
                type='text'
                autoComplete='cc-name'
                value={values.cardholder_name}
                onChange={handleChange}
                required
                onBlur={handleBlur}
                error={
                  touched.cardholder_name && errors.cardholder_name
                    ? true
                    : false
                }
                helperText={touched.cardholder_name && errors.cardholder_name}
                style={{
                  gridColumn: '1 / -1',
                  fontFamily: appConfig.styleConfig.primaryFontFontFamily,
                }}
              />
              {
                // for testing only, postal code needs to be V8T 4M3
              }
              {isPostalCodeEnabled ? (
                <TextField
                  id='PaymentMethods-PostalCode'
                  label='Postal Code'
                  name='postal_code'
                  type='text'
                  // autoComplete="cc-name"
                  // value={values.postal_code}
                  value={postalCode}
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                    // handleChange(e);
                    setPostalCode(e.target.value);
                  }}
                  required
                  onBlur={handleBlur}
                  error={
                    touched.postal_code && errors.postal_code ? true : false
                  }
                  helperText={touched.postal_code && errors.postal_code}
                  style={{
                    gridColumn: '1 / -1',
                    fontFamily: appConfig.styleConfig.primaryFontFontFamily,
                  }}
                />
              ) : null}
              {isAddressEnabled ? (
                <Fragment>
                  <NumberFormat
                    id='PaymentMethods-StreetNumber'
                    label='Street Number'
                    name='street_number'
                    type='text'
                    // autoComplete="cc-name"
                    value={
                      // values.street_number
                      streetNumber
                    }
                    onChange={(e) => {
                      // e.target.value = e.target.value.toUpperCase();
                      // handleChange(e);
                      setStreetNumber(e.target.value);

                      // handleChange
                    }}
                    customInput={TextField}
                    required
                    onBlur={handleBlur}
                    error={
                      touched.street_number && errors.street_number
                        ? true
                        : false
                    }
                    helperText={touched.street_number && errors.street_number}
                    style={{
                      gridColumn: '1 / -1',
                      fontFamily: appConfig.styleConfig.primaryFontFontFamily,
                    }}
                  />
                  <TextField
                    id='PaymentMethods-StreetName'
                    label='Street Name'
                    name='street_name'
                    type='text'
                    // autoComplete="cc-name"
                    value={
                      // values.street_name
                      streetName
                    }
                    onChange={
                      (e) => {
                        setStreetName(e.target.value);
                      }
                      // handleChange
                    }
                    required
                    onBlur={handleBlur}
                    error={
                      touched.street_name && errors.street_name ? true : false
                    }
                    helperText={touched.street_name && errors.street_name}
                    style={{
                      gridColumn: '1 / -1',
                      fontFamily: appConfig.styleConfig.primaryFontFontFamily,
                    }}
                  />
                </Fragment>
              ) : null}
            </FieldsWrapper>
            {appConfig.isPreAuthMessageEnabled ? (
              <div id='PaymentMethod-PreAuthMessage'>
                On submission of your credit card information, we will be
                performing validation for fraud prevention measures. You may see
                a temporary $0.50 hold on your statement. This is not a charge
                and will be removed in 3-5 days.
                {appConfig.appSource === 'chop' ? (
                  <div style={{ marginTop: '10px' }}>
                    Payment methods accepted: VISA & MC
                  </div>
                ) : null}
              </div>
            ) : null}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '12px',
              }}
            >
              <FlatButton
                id='PaymentMethods-CancelButton'
                onClick={handleCancel}
                secondary
                type='button'
                disabled={isRequesting}
              >
                Cancel
              </FlatButton>
              <FlatButton
                id='PaymentMethods-SaveButton'
                onClick={handleSubmit}
                type='submit'
                disabled={isRequesting}
                showLoading={isRequesting}
              >
                Save
              </FlatButton>
            </div>
          </Wrapper>
        </form>
      )}
    </Formik>
  );
};

export default PaymentMethodForm;
